import { TOKEN_KEY } from '@/consts/const';
import { sendPreRequestAuditIfMatched } from '@/utils/audit/sendPreRequestAudit';
import { toast } from 'react-toastify';

/**
 * Fetches file bytes via /api/gallery/download (same-origin + auth).
 * Used for downloads and for PDF preview in an iframe (avoids X-Frame-Options
 * blocking when embedding S3/legacy URLs directly).
 */
export async function fetchClientFileBlob(fileUrl: string, filename: string): Promise<Blob> {
  if (!fileUrl) throw new Error('Missing file URL');

  const baseUrl = process.env.NEXT_PUBLIC_API_URL;
  const accessToken = localStorage.getItem(TOKEN_KEY);
  const params = new URLSearchParams({ url: fileUrl, filename });
  const downloadPath = `/api/gallery/download?${params.toString()}`;

  await sendPreRequestAuditIfMatched(downloadPath, {
    method: 'GET',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  const response = await fetch(`${baseUrl}${downloadPath}`, {
    method: 'GET',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
}

/**
 * Downloads a client file (support document or passport) via the backend
 * proxy endpoint /api/gallery/download, which fetches the file server-side and
 * returns it with Content-Disposition: attachment — avoiding CORS issues
 * when the file is hosted on S3 or legacy storage.
 */
export const downloadClientFile = async (
  fileUrl: string | null,
  filename: string,
  id: number,
  onSuccess?: (id: number) => void
) => {
  if (!fileUrl) {
    toast.error(`Failed to download ${filename}`);
    return;
  }

  try {
    const blob = await fetchClientFileBlob(fileUrl, filename);
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);

    onSuccess?.(id);
  } catch {
    toast.error(`Failed to download ${filename}`);
  }
};
